import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

// Packages
import { Card, Col, Divider, Form, Row, Select, Space, Spin, Table, Typography } from 'antd';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';

// Actions
import { get_file_info, get_file_logs } from '../../actions/filesActions';
import { search_user } from '../../actions/usersActions';
import isEmpty from '../../utils/isEmpty';

const FileLogs = (props) => {
	const { messageApi } = props;

	const { file_id } = useParams();

	const navigate = useNavigate();

	const dispatch = useDispatch();

	const { isAdminUser } = useSelector(state => state.auth);
	
	const { user_staff } = useSelector(state => state.staffs);

	const { global_data } = useSelector(state => state.global);

	const { search_user_loading, users_found } = useSelector(state => state.users);

	const { file_info_loading, file_info, file_logs_loading, file_logs } = useSelector(state => state.trainingFiles);

	const [loading, setLoading] = useState(true);
	const [skip, setSkip] = useState(0);
	const [pageSize, setPageSize] = useState(10);
	const [user, setUser] = useState('');

	useEffect(() => {
		dispatch(get_file_info(file_id));
	}, []);

	useEffect(() => {
		if (!isAdminUser && !isEmpty(user_staff) && user_staff?.staff_type !== 1) {
			navigate('/access-denied');
			return; // Avoid continuing with setLoading if redirected
		}
		setLoading(false);
	}, [isAdminUser, user_staff, navigate]);

	useEffect(() => {
		const filters = {
			file: file_id,
			skip: skip,
			limit: pageSize,
			user: user
		};
		dispatch(get_file_logs(filters));
	}, [user]);
	
	const columns = [
		{
			title: global_data?.fileLogs?.user,
			dataIndex: ['user', 'username'],
			key: 'user'
		},
		{
			title: global_data?.fileLogs?.downloadDate,
			dataIndex: ['date', '$date'],
			key: 'user',
			render: (date) => (
				<Typography.Text>{new Date(date).toLocaleString(navigator.language, { hour12: true })}</Typography.Text>
			)
		}
	];

	const handleTableChange = (pagination, filters, sorter) => {
		const skip = (pagination.current - 1) * pageSize;
		setSkip(skip);

		const logsFilters = { file: file_id, skip: skip, limit: pageSize };
		dispatch(get_file_logs(logsFilters));
	};

	const debouncedSearchUser = debounce((dispatch, value) => {
		const filters = {
			organization: file_info?.organization?._id?.$oid,
			username: value
		};
		dispatch(search_user(filters));
	}, 500);

	const onSearchUser = (value) => {
		if (value && value.length > 0) {
			const username = value.replace(/-/g, '?');
			debouncedSearchUser(dispatch, username);
		}
	};

	if (loading) return  <Spin fullscreen />;

	return (
		<Row justify='center'>
			<Col lg={17} md={17} sm={24} xs={24}>
				<Typography.Title>{global_data?.fileLogs?.title}</Typography.Title>
				<Typography.Text>{global_data?.fileLogs?.description}</Typography.Text>

				<Card
					style={{ marginTop: '2rem' }}
					loading={file_info_loading}
					title={global_data?.fileLogs?.fileInfo}
				>
					<Typography.Paragraph copyable={{ text: file_info?._id?.$oid, tooltips: [global_data?.utils?.copyId, global_data?.utils?.idCopied] }}>
						<strong>{global_data?.files?.formInputName}:</strong> {file_info?.name}
					</Typography.Paragraph>
					<Typography.Paragraph><strong>{global_data?.files?.formInputDescription}:</strong> {file_info?.description}</Typography.Paragraph>
					<Typography.Paragraph><strong>{global_data?.fileLogs?.creationDate}:</strong> {new Date(file_info?.date?.$date).toLocaleString(navigator.language, { hour12: true })}</Typography.Paragraph>
					<Divider orientation='left'>{global_data?.fileLogs?.organizationInfo}</Divider>
					<Typography.Paragraph><strong>{global_data?.organizationTerms?.workspace}:</strong> {file_info?.workspace?.name}</Typography.Paragraph>
					{file_info?.region !== undefined && (
						<div>
							<Divider orientation='left'>{global_data?.organizationTerms?.areas}</Divider>
							<Space size={'large'} wrap>
								<Typography.Paragraph><strong>{global_data?.organizationTerms?.region}:</strong> {file_info?.region?.name}</Typography.Paragraph>
								{file_info?.zone !== undefined && (
									<Typography.Paragraph><strong>{global_data?.organizationTerms?.zone}:</strong> {file_info?.zone?.name}</Typography.Paragraph>
								)}
							</Space>
						</div>
					)}
				</Card>

				<Form.Item
					style={{ margin: '2rem auto' }}
					label={`${global_data?.actionButtons?.search} ${global_data?.fileLogs?.user}`}
				>
					<Select
						style={{ width: '100%' }}
						showSearch
						placeholder={`${global_data?.form?.inputPlaceholder} ${global_data?.fileLogs?.username}`}
						optionFilterProp='children'
						filterOption={false}
						onSearch={(value) => onSearchUser(value)}
						loading={search_user_loading}
						options={users_found.users.map(user => ({ value: user.id, label: user.username }))}
						onChange={(value) => setUser(value ? value : '')}
						allowClear={true}
					/>
				</Form.Item>

				<Spin spinning={file_logs_loading} tip={global_data?.utils?.loading} size='large'>
					<Table
						columns={columns}
						dataSource={file_logs.logs}
						onChange={handleTableChange}
						scroll={{ x: 'auto' }}
						pagination={{
							pageSize: pageSize,
							total: file_logs.count,
							showTotal: (total, range) => `${range[0]} - ${range[1]} ${global_data?.table?.paginationOf} ${total}`,
							hideOnSinglePage: true
						}}
					/>
				</Spin>
			</Col>
		</Row>
	)
}

FileLogs.propTypes = {
	messageApi: PropTypes.object.isRequired
}

export default FileLogs;
